/**
 * section-header.js
 * SafetyRail Source — Mega menu controller
 *
 * Desktop: hover opens mega, L2 hover/click swaps L3 panel
 * Mobile:  hamburger drawer, mega accordion, L2 accordion → L3 inline
 *
 * No dependencies. Vanilla JS.
 */

(function () {
  'use strict';

  /* ── Helpers ──────────────────────────────────────────── */
  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const isMobile = () => window.innerWidth <= 900;

  /* ════════════════════════════════════════════════════════
     MOBILE SEARCH MODAL — initialised first so it works even
     if other header elements (toggle/menu) are missing.
  ════════════════════════════════════════════════════════ */
  initMobileSearch();

  /* ── Elements ─────────────────────────────────────────── */
  const header  = $('#site-header');
  const toggle  = $('.nav__toggle', header);
  const menu    = $('#nav-menu');
  const overlay = $('#nav-overlay');

  if (!header || !toggle || !menu) return;

  /* ════════════════════════════════════════════════════════
     MOBILE DRAWER
  ════════════════════════════════════════════════════════ */
  function openMobileMenu () {
    menu.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    overlay.classList.add('is-visible');
    overlay.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
  }

  function closeMobileMenu () {
    menu.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    overlay.classList.remove('is-visible');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    closeAllMegaMenus();
  }

  toggle.addEventListener('click', () => {
    menu.classList.contains('is-open') ? closeMobileMenu() : openMobileMenu();
  });
  overlay.addEventListener('click', closeMobileMenu);

  /* ════════════════════════════════════════════════════════
     MEGA MENU — open / close
  ════════════════════════════════════════════════════════ */
  let hoverTimer = null;
  const HOVER_OPEN_DELAY  = 80;
  const HOVER_CLOSE_DELAY = 140;

  function openMegaMenu (btn, panel) {
    closeAllMegaMenus(btn);
    btn.setAttribute('aria-expanded', 'true');
    panel.removeAttribute('hidden');
    requestAnimationFrame(() => panel.classList.add('is-open'));
  }

  function closeMegaMenu (btn, panel) {
    btn.setAttribute('aria-expanded', 'false');
    panel.classList.remove('is-open');
    // Reset L3 state so next open starts clean (no L3 panel pre-showing)
    $$('.mega-menu__l3-panel.is-active', panel).forEach(p => {
      p.classList.remove('is-active');
      p.setAttribute('hidden', '');
    });
    $$('.mega-menu__l2-item.is-active', panel).forEach(i => i.classList.remove('is-active'));
    const l3Zone = $('.mega-menu__zone--l3', panel);
    if (l3Zone) l3Zone.classList.remove('has-active');
    panel.addEventListener('transitionend', function h () {
      if (!panel.classList.contains('is-open')) panel.setAttribute('hidden', '');
      panel.removeEventListener('transitionend', h);
    });
  }

  function closeAllMegaMenus (exceptBtn = null) {
    $$('.nav__menu-link--parent[aria-expanded="true"]', header).forEach(btn => {
      if (btn === exceptBtn) return;
      const panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (panel) closeMegaMenu(btn, panel);
    });
  }

  /* ════════════════════════════════════════════════════════
     L2 ↔ L3 PANEL SWITCHING
     NEW: clicking or hovering an L2 item activates its L3 panel
  ════════════════════════════════════════════════════════ */
  function activateL2Item (item, megaPanel) {
    // Deactivate sibling L2 items
    $$('.mega-menu__l2-item.is-active', megaPanel).forEach(el => {
      el.classList.remove('is-active');
      const btn = $('.mega-menu__l2-link--has-l3', el);
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });

    // Deactivate all L3 panels in this mega
    $$('.mega-menu__l3-panel.is-active', megaPanel).forEach(el => {
      el.classList.remove('is-active');
      el.setAttribute('hidden', '');
    });

    // Activate clicked/hovered item
    item.classList.add('is-active');
    const btn = $('.mega-menu__l2-link--has-l3', item);
    if (btn) btn.setAttribute('aria-expanded', 'true');

    // Show or hide L3 zone based on whether this L2 item has L3 children
    const targetId = item.dataset.l3Target;
    const l3Zone = $('.mega-menu__zone--l3', megaPanel);
    if (l3Zone) l3Zone.classList.toggle('has-active', !!targetId);

    if (targetId) {
      const l3Panel = document.getElementById(targetId);
      if (l3Panel) {
        l3Panel.classList.add('is-active');
        l3Panel.removeAttribute('hidden');
        const itemCount = l3Panel.querySelectorAll('.mega-menu__l3-link').length;
        l3Panel.classList.toggle('single-col', itemCount <= 4);
      }
    }
  }

  /* ════════════════════════════════════════════════════════
     WIRE UP EACH L1 PARENT BUTTON
  ════════════════════════════════════════════════════════ */
  $$('.nav__menu-item--has-dropdown', menu).forEach(item => {
    const btn   = $('.nav__menu-link--parent', item);
    const panel = btn && document.getElementById(btn.getAttribute('aria-controls'));
    if (!btn || !panel) return;

    /* ── Desktop: hover intent ── */
    item.addEventListener('mouseenter', () => {
      if (isMobile()) return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(() => openMegaMenu(btn, panel), HOVER_OPEN_DELAY);
    });
    item.addEventListener('mouseleave', () => {
      if (isMobile()) return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(() => {
        if (!panel.matches(':hover')) closeMegaMenu(btn, panel);
      }, HOVER_CLOSE_DELAY);
    });
    panel.addEventListener('mouseenter', () => { if (!isMobile()) clearTimeout(hoverTimer); });
    panel.addEventListener('mouseleave', () => {
      if (isMobile()) return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(() => closeMegaMenu(btn, panel), HOVER_CLOSE_DELAY);
    });

    /* ── Click / keyboard toggle (desktop + mobile) ──
       Mobile behavior is driven by two Header-section settings
       exposed via data-attrs on .site-header:
         data-mobile-parent-navigable  "true" | "false"
         data-mobile-submenu-trigger   "full_row" | "arrow_only"

       Matrix:
         A=OFF, B=full_row   → tap anywhere opens submenu (legacy default)
         A=OFF, B=arrow_only → tap arrow opens submenu; text tap = no-op
         A=ON,  B=*          → text tap navigates; arrow tap opens submenu
       (A overrides B whenever the user taps the parent's text portion.) */
    btn.addEventListener('click', e => {
      if (!isMobile()) {
        // Desktop: let the <a> navigate to its page naturally
        return;
      }

      const header = btn.closest('.site-header');
      const allowNav    = !!(header && header.dataset.mobileParentNavigable === 'true');
      const triggerMode = (header && header.dataset.mobileSubmenuTrigger) || 'full_row';
      const clickedArrow = !!(e.target && e.target.closest && e.target.closest('.nav__arrow-tap'));

      const toggle = () => {
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        isOpen ? closeMegaMenu(btn, panel) : openMegaMenu(btn, panel);
      };

      if (allowNav) {
        // A=ON: text → navigate (default), arrow → open submenu
        if (clickedArrow) {
          e.preventDefault();
          toggle();
        }
        return;
      }

      // A=OFF
      if (triggerMode === 'arrow_only' && !clickedArrow) {
        // Arrow-only mode + text tap → suppress navigation, no submenu open
        e.preventDefault();
        return;
      }
      e.preventDefault();
      toggle();
    });

    /* ── Escape ── */
    const escClose = e => { if (e.key === 'Escape') { closeMegaMenu(btn, panel); btn.focus(); } };
    btn.addEventListener('keydown', escClose);
    panel.addEventListener('keydown', escClose);

    /* ── Wire L2 items inside this mega ── */
    wireL2Items(panel);

    /* ── On open: initialise first L2 active state ── */
    // (Already set in Liquid via is-active / not-hidden, JS just keeps it in sync)
  });

  /* ════════════════════════════════════════════════════════
     L2 ITEM WIRING (desktop hover + click, mobile accordion)
  ════════════════════════════════════════════════════════ */
  function wireL2Items (megaPanel) {
    const l2Items = $$('.mega-menu__l2-item', megaPanel);

    l2Items.forEach(l2Item => {
      const l2Btn = $('.mega-menu__l2-link', l2Item);
      if (!l2Btn) return;

      /* ── Desktop: hover activates L3 panel (or hides it for items without L3) ── */
      l2Item.addEventListener('mouseenter', () => {
        if (isMobile()) return;
        activateL2Item(l2Item, megaPanel);
      });

      /* ── Click: on desktop (plain link L2) navigates; on button L2, toggles ── */
      l2Btn.addEventListener('click', e => {
        if (!isMobile()) {
          // Desktop: if it's a button (has L3), just activate – don't navigate
          if (l2Btn.tagName === 'BUTTON') {
            e.preventDefault();
            activateL2Item(l2Item, megaPanel);
          }
          // If it's an <a> with no L3, let it navigate naturally
          return;
        }

        // ── Mobile: L2 accordion toggle (mirrors L1 matrix) ──
        // L2 links are always <a> in this theme. Before, the mobile toggle
        // was gated on tagName === 'BUTTON' and never ran. Now we gate on
        // whether the L2 has L3 children (has-l3 class) and apply the
        // same Header-section settings matrix as L1.
        const hasL3 = l2Item.classList.contains('has-l3');
        if (!hasL3) {
          // No L3 children — let the <a> navigate naturally
          return;
        }

        const l2Header     = l2Btn.closest('.site-header');
        const l2AllowNav   = !!(l2Header && l2Header.dataset.mobileParentNavigable === 'true');
        const l2TriggerMode = (l2Header && l2Header.dataset.mobileSubmenuTrigger) || 'full_row';
        const l2ClickedArrow = !!(e.target && e.target.closest && e.target.closest('.nav__arrow-tap'));

        const toggleL3 = () => {
          const targetId = l2Item.dataset.l3Target;
          const l3Panel  = targetId && document.getElementById(targetId);
          if (!l3Panel) return;
          const isOpen = l3Panel.classList.contains('is-open-mobile');

          // Close all other open L3 panels in this mega
          $$('.mega-menu__l3-panel.is-open-mobile', megaPanel).forEach(p => {
            p.classList.remove('is-open-mobile', 'is-active');
            p.setAttribute('hidden', '');
          });
          $$('.mega-menu__l2-item.is-active', megaPanel).forEach(i => i.classList.remove('is-active'));
          $$('.mega-menu__l2-link--has-l3[aria-expanded="true"]', megaPanel).forEach(b => {
            b.setAttribute('aria-expanded', 'false');
          });

          if (!isOpen) {
            l2Item.classList.add('is-active');
            l2Btn.setAttribute('aria-expanded', 'true');
            l3Panel.classList.add('is-open-mobile', 'is-active');
            l3Panel.removeAttribute('hidden');
            injectL3AfterL2(l2Item, l3Panel);
          }
        };

        if (l2AllowNav) {
          // A=ON: tap text → navigate (default); tap arrow → toggle L3
          if (l2ClickedArrow) {
            e.preventDefault();
            toggleL3();
          }
          return;
        }

        // A=OFF
        if (l2TriggerMode === 'arrow_only' && !l2ClickedArrow) {
          // Arrow-only + text tap → suppress navigation, don't open
          e.preventDefault();
          return;
        }
        e.preventDefault();
        toggleL3();
      });
    });

    /* ── Close menu after a leaf link is clicked ──
       L3 links (e.g. FAQ subsection anchors like #fixed-ladder) and
       plain L2 links without children are anchor / page links — they
       should dismiss the menu the same way other navs do.
       • Desktop: close the mega-menu dropdown.
       • Mobile : close the mobile drawer entirely (the user is
                  navigating away — leaving the drawer sticky over
                  the new page is the bug we're fixing here).
       L2 toggles that open an L3 submenu are skipped — those still
       toggle the accordion in-place without closing anything. */
    megaPanel.addEventListener('click', function (e) {
      const link = e.target.closest('a[href]');
      if (!link) return;
      // Don't close on L2 toggles that open an L3 submenu
      if (link.classList.contains('mega-menu__l2-link--has-l3')) return;

      if (isMobile()) {
        closeMobileMenu();
      } else {
        closeAllMegaMenus();
      }
    });
  }

  /**
   * NEW: On mobile the L3 panel lives in a separate zone div.
   * For accordion UX we move it into the L2 list directly after its parent item.
   * We track whether it's been moved to avoid double-move.
   */
  function injectL3AfterL2 (l2Item, l3Panel) {
    if (l3Panel.dataset.injected === 'true') return;
    // Only run on mobile
    if (!isMobile()) return;
    l2Item.after(l3Panel);
    l3Panel.dataset.injected = 'true';
  }

  /* ════════════════════════════════════════════════════════
     CLOSE ON OUTSIDE CLICK (desktop)
  ════════════════════════════════════════════════════════ */
  document.addEventListener('click', e => {
    if (!isMobile() && !header.contains(e.target)) closeAllMegaMenus();
  });

  /* ════════════════════════════════════════════════════════
     RESIZE: cross-boundary cleanup
  ════════════════════════════════════════════════════════ */
  let lastMobile = isMobile();
  window.addEventListener('resize', () => {
    const nowMobile = isMobile();
    if (nowMobile !== lastMobile) {
      lastMobile = nowMobile;
      closeMobileMenu();
      closeAllMegaMenus();

      // Reset any injected L3 panels back to their original zone on desktop
      $$('.mega-menu__l3-panel[data-injected="true"]').forEach(panel => {
        const megaMenu    = panel.closest('.mega-menu');
        const l3Zone      = megaMenu && $('.mega-menu__zone--l3', megaMenu);
        if (l3Zone && !l3Zone.contains(panel)) l3Zone.appendChild(panel);
        panel.dataset.injected = 'false';
        panel.classList.remove('is-open-mobile');
      });

      // Re-apply default active state on desktop
      $$('.mega-menu').forEach(megaPanel => {
        const firstL2 = $('.mega-menu__l2-item', megaPanel);
        if (firstL2) activateL2Item(firstL2, megaPanel);
      });
    }
  }, { passive: true });

  /* ════════════════════════════════════════════════════════
     KEYBOARD: Tab out closes mega, Arrow keys navigate L3
  ════════════════════════════════════════════════════════ */
  document.addEventListener('keydown', e => {
    if (e.key === 'Tab') {
      setTimeout(() => {
        if (!header.contains(document.activeElement)) closeAllMegaMenus();
      }, 0);
    }
  });

  $$('.mega-menu').forEach(megaMenu => {
    megaMenu.addEventListener('keydown', e => {
      if (!['ArrowUp', 'ArrowDown'].includes(e.key)) return;
      e.preventDefault();
      const focusable = $$('a, button', megaMenu).filter(el => !el.closest('[hidden]'));
      const idx = focusable.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') focusable[Math.min(idx + 1, focusable.length - 1)]?.focus();
      if (e.key === 'ArrowUp')   focusable[Math.max(idx - 1, 0)]?.focus();
    });
  });

  // No L3 panel init needed — single-layer dropdowns only

  /* ════════════════════════════════════════════════════════
     MOBILE SEARCH + SEARCHANISE BRIDGE
     On ≤900px the Searchanise Search & Filter app injects its
     own mobile search panel (.snize-mobile-top-panel) with input
     #snize-mobile-search-input inside a full-screen overlay
     (#snize-instant-search-results).

     We keep our theme's search UI visible (matches design),
     hide Searchanise's top panel via CSS (body.srail-search-open), and
     bridge keystrokes from our input → Searchanise's hidden input so
     results render below our form. See section-header.css for the
     companion CSS rules.
  ════════════════════════════════════════════════════════ */
  /* ════════════════════════════════════════════════════════
     STICKY-STATE MIRROR → body.header-sticky
     ──────────────────────────────────────────────────────────
     Floating UI (e.g. the Vimotia PiP play button) needs to know
     when the sticky header is currently stuck to the top so it
     can shift its `top` offset accordingly. We mirror that state
     to a body class — clean CSS hook, no fragile sibling selectors.
     Only runs when the merchant has the sticky header enabled.
  ════════════════════════════════════════════════════════ */
  if (header.classList.contains('site-header--sticky')) {
    let lastStuck = null;
    const STICKY_THRESHOLD = 8; // px — small grace zone for momentum scroll
    const updateStickyState = () => {
      const stuck = window.scrollY > STICKY_THRESHOLD;
      if (stuck === lastStuck) return;
      lastStuck = stuck;
      document.body.classList.toggle('header-sticky', stuck);
    };
    updateStickyState();
    window.addEventListener('scroll', updateStickyState, { passive: true });
  }

  function initMobileSearch () {
    const searchTrigger = document.querySelector('.nav__search-trigger');
    const searchForm    = document.getElementById('nav-search');

    if (!searchTrigger || !searchForm) return;

    const searchInput = searchForm.querySelector('.nav__search-input');

    /* Attach the keystroke forwarder IMMEDIATELY — not gated on
       Searchanise's DOM appearing. syncToSnize() polls for readiness
       (waitForSnizeReady) and replays the value the moment Searchanise
       mounts, so even keystrokes that land before the bundle finishes
       loading are captured. Previously this listener was attached
       inside initBridge() which only ran AFTER snize injected its
       hidden input — meaning early keystrokes on iOS were silently
       dropped, requiring the user to tap again. */
    if (searchInput) {
      searchInput.addEventListener('input', function () { syncToSnize(); });
      searchInput.addEventListener('keyup', function () { syncToSnize(); });
    }

    /* ── Searchanise bridge ────────────────────────────────── */
    let bridgeReady   = false;  // true once snize input is wired
    let snizeInputRef = null;   // cached ref to #snize-mobile-search-input
    let snizeActivated = false; // flips true after first activation tap (per open)

    /* ── Debug logger ───────────────────────────────────────────
       Off by default. Enable on iPhone for one session by appending
       `?srdbg=1` to the URL OR running `localStorage.srdbg='1'` in
       Safari Web Inspector. Safe to leave in production — zero cost
       when disabled. */
    const SR_DBG = (function () {
      try {
        return /[?&]srdbg=1/.test(location.search) ||
               (window.localStorage && localStorage.getItem('srdbg') === '1');
      } catch (_) { return false; }
    })();
    function srLog () {
      if (!SR_DBG) return;
      try { console.log.apply(console, ['[srail]'].concat([].slice.call(arguments))); } catch (_) {}
    }

    /* ── snizeReadyState ────────────────────────────────────────
       Returns the FULL set of Searchanise mobile DOM nodes plus a
       boolean `ready` flag that's true only when ALL prerequisites
       for predictive search are met:
         1. #snize-mobile-search-input exists
         2. #snize-instant-search-results overlay exists
         3. the hidden input is connected to the live DOM
            (defends against detached-but-cached references)
       Used by waitForSnizeReady so we don't activate / dispatch
       events into a half-mounted Searchanise. */
    function snizeReadyState () {
      const snizeInput = document.getElementById('snize-mobile-search-input');
      const overlay    = document.getElementById('snize-instant-search-results');
      return {
        input:   snizeInput,
        overlay: overlay,
        /* overlay intentionally NOT required: on Android Chrome, Searchanise
           injects #snize-instant-search-results lazily (only after a real
           focus/interaction on its input). Requiring the overlay here meant
           waitForSnizeReady always timed out on Android, so the focus-dance
           and activation sequence never ran. The overlay will appear on its
           own once Searchanise is activated via the focus-dance below. */
        ready:   !!(snizeInput && snizeInput.isConnected)
      };
    }

    /* ── waitForSnizeReady: bounded polling for FULL Searchanise readiness
       ────────────────────────────────────────────────────────────────────
       Searchanise injects #snize-mobile-search-input AND its overlay
       (#snize-instant-search-results) asynchronously after its bundle
       loads. A single setTimeout retry isn't enough on slow iOS
       networks; activating against a half-mounted Searchanise (input
       present but overlay not yet rendered, or vice-versa) is exactly
       what causes the "type → no suggestions until you tap again"
       symptom on iPhone.

       Strategy:
         - Poll every 50 ms, max 20 attempts (~1 s total).
         - Each tick calls initBridge() (cheap — has bridgeReady guard).
         - Resolve cb(true, state) the moment ALL readiness predicates
           hold; resolve cb(false, state) on timeout.
         - Multiple concurrent callers are coalesced into one poll loop. */
    let snizePollId      = null;
    const snizePollCallbacks = [];
    function waitForSnizeReady (cb) {
      const initial = snizeReadyState();
      if (initial.ready) { try { cb(true, initial); } catch (_) {} return; }
      snizePollCallbacks.push(cb);
      if (snizePollId) return; // already polling — dedupe
      let attempts = 0;
      const MAX = 20;
      const flush = function (ok, state) {
        const cbs = snizePollCallbacks.splice(0, snizePollCallbacks.length);
        cbs.forEach(function (fn) { try { fn(ok, state); } catch (_) {} });
      };
      snizePollId = setInterval(function () {
        attempts++;
        initBridge();
        const s = snizeReadyState();
        if (s.ready) {
          clearInterval(snizePollId);
          snizePollId = null;
          console.log('[SRAIL SEARCH] snize ready after', attempts, 'attempts', {
            inputConnected: !!(s.input && s.input.isConnected),
            overlay: !!s.overlay
          });
          flush(true, s);
        } else if (attempts >= MAX) {
          clearInterval(snizePollId);
          snizePollId = null;
          console.log('[SRAIL SEARCH] snize NOT ready after', attempts, 'attempts', {
            input: !!s.input,
            inputConnected: !!(s.input && s.input.isConnected),
            overlay: !!s.overlay
          });
          flush(false, s);
        }
      }, 50);
    }

    /* ── dispatchSnizeKeystrokeLifecycle ─────────────────────────
       Fires the FULL set of events Searchanise (across versions) is
       known to listen for. Using a synthetic `Event('input')` alone
       is unreliable on iOS — Searchanise on certain builds requires
       a true InputEvent with `inputType: 'insertText'` to recompute,
       and some versions sniff focus/keyboard events to mark the
       input as "active". Order matters: focus signals → keydown/
       keypress → input → keyup → change.
       Wrapped in try/catch per event because old Safari throws on
       missing constructor options. */
    function dispatchSnizeKeystrokeLifecycle () {
      if (!snizeInputRef) return;
      const val = searchInput.value;
      const fire = function (ev) {
        try { snizeInputRef.dispatchEvent(ev); } catch (_) {}
      };
      try { fire(new FocusEvent('focus',   { bubbles: false })); } catch (_) {}
      try { fire(new FocusEvent('focusin', { bubbles: true  })); } catch (_) {}
      try { fire(new KeyboardEvent('keydown',  { bubbles: true, cancelable: true })); } catch (_) {}
      try { fire(new KeyboardEvent('keypress', { bubbles: true, cancelable: true })); } catch (_) {}
      let inputEv;
      try {
        inputEv = new InputEvent('input', {
          bubbles: true,
          cancelable: false,
          inputType: 'insertText',
          data: val
        });
      } catch (_) {
        inputEv = new Event('input', { bubbles: true });
      }
      fire(inputEv);
      try { fire(new KeyboardEvent('keyup', { bubbles: true, cancelable: true })); } catch (_) {}
      try { fire(new Event('change', { bubbles: true })); } catch (_) {}
      srLog('dispatchSnizeKeystrokeLifecycle', {
        val: val,
        snizeVal: snizeInputRef.value,
        snizeOverlay: !!document.getElementById('snize-instant-search-results')
      });
    }

    function initBridge () {
      if (bridgeReady) return;
      const snizeInput = document.getElementById('snize-mobile-search-input');
      if (!snizeInput) return;
      console.log('[SRAIL SEARCH] initBridge wired — snizeInputRef cached');

      bridgeReady   = true;
      snizeInputRef = snizeInput;
      bridgeObserver.disconnect();

      /* Focus-dance with retry loop — Android Chrome timing fix.
         Searchanise binds its listeners on #snize-mobile-search-input
         asynchronously (setTimeout/rAF) after DOM injection. Each _dance()
         focuses the snize input so Searchanise's focus handler fires.
         The steal-back is intentionally DELAYED 50ms (see focus-steal
         listener below) — Searchanise's async activation callback checks
         document.activeElement; an immediate steal makes it see searchInput
         and abort overlay creation. 50ms gives it time to complete.
         Stop conditions: overlay appeared, panel closed, user typed, 15 tries. */
      let _stealBackId = null;
      if (searchForm.classList.contains('is-open') && window.innerWidth <= 900) {
        const _dance = () => {
          try { snizeInput.focus({ preventScroll: true }); } catch (_) {}
        };
        _dance();
        let _attempts = 0;
        const _retryId = setInterval(() => {
          _attempts++;
          if (
            _attempts > 15 ||
            !searchForm.classList.contains('is-open') ||
            window.innerWidth > 900 ||
            document.getElementById('snize-instant-search-results') ||
            searchInput.value.trim()
          ) {
            clearInterval(_retryId);
            return;
          }
          _dance();
        }, 150);
      }

      /* Note: searchInput input/keyup listeners are attached
         unconditionally at the top of initMobileSearch — see comment
         block there. Don't re-attach here (would fire syncToSnize
         twice per keystroke). */

      /* Catch-up: if the user already typed BEFORE Searchanise's DOM
         was ready (snizeInputRef was null then), syncToSnize returned
         early and the typed value never reached snize. Sync once now
         that we have the reference, so existing input is honoured. */
      if (searchInput.value) {
        syncToSnize();
      }

      /* Steal focus back from snize input after 50ms — not immediately.
         The delay is critical on Android Chrome: Searchanise's focus handler
         schedules overlay creation asynchronously and checks
         document.activeElement in that callback. An immediate steal puts
         searchInput as activeElement before the callback runs → Searchanise
         aborts creation. 50ms lets it complete. clearTimeout + reschedule
         means repeated _dance() calls just reset the clock, not stack steals.
         The snize input is off-screen (position:fixed left:-9999px) so the
         keyboard stays open on Android during the 50ms gap. */
      snizeInput.addEventListener('focus', () => {
        if (searchForm.classList.contains('is-open') && window.innerWidth <= 900) {
          clearTimeout(_stealBackId);
          _stealBackId = setTimeout(() => {
            if (searchForm.classList.contains('is-open') && window.innerWidth <= 900) {
              searchInput.focus({ preventScroll: true });
            }
          }, 50);
        }
      });
    }

    function syncToSnize () {
      const val = searchInput ? searchInput.value : '';
      console.log('[SRAIL SEARCH] syncToSnize', {
        val: val,
        hasRef: !!snizeInputRef,
        bridgeReady: bridgeReady,
        activated: snizeActivated
      });
      if (!snizeInputRef) {
        /* iOS Safari race: the user can type the first character BEFORE
           Searchanise's hidden input + overlay are both mounted. Poll
           with the FULL readiness check (waitForSnizeReady) for up to
           ~1 second. The moment Searchanise is fully ready we replay
           this sync, so the first typed character lands without
           requiring a second tap. */
        waitForSnizeReady(function (ok) {
          console.log('[SRAIL SEARCH] syncToSnize replay after wait', { ok: ok });
          if (ok) syncToSnize();
        });
        return;
      }

      /* 1. Set value FIRST so any activation event fires against an
         input that already has the user's query — Searchanise may
         skip computing results if value is empty when it activates. */
      snizeInputRef.value = searchInput.value;

      /* 2. First-keystroke activation. Searchanise on iOS doesn't
         appear to react to our setTimeout-deferred activateSnizePanel
         until the user lays a real finger on the search bar/button.
         Best guess: it listens for clicks on the search form/button
         via delegation. Replicate that by firing mousedown/mouseup
         on our search button (NOT click — that would submit) and a
         full simulateTap on the snize input, in the same tick as
         the first user keystroke (which is itself a trusted gesture
         that may carry the user-gesture trust into our handlers). */
      if (!snizeActivated) {
        snizeActivated = true;

        /* Tap on our search button (mousedown + mouseup only —
           skipping click avoids form submission to /search). */
        const ourSearchBtn = searchForm.querySelector('.nav__search-btn');
        if (ourSearchBtn) {
          const btnOpts = { bubbles: true, cancelable: true, view: window };
          try { ourSearchBtn.dispatchEvent(new MouseEvent('mousedown', btnOpts)); } catch (_) {}
          try { ourSearchBtn.dispatchEvent(new MouseEvent('mouseup',   btnOpts)); } catch (_) {}
          try { ourSearchBtn.dispatchEvent(new PointerEvent('pointerdown', btnOpts)); } catch (_) {}
          try { ourSearchBtn.dispatchEvent(new PointerEvent('pointerup',   btnOpts)); } catch (_) {}
        }

        /* Full simulated tap on snize input (covers Searchanise listeners
           bound directly on its input). */
        try { simulateTap(snizeInputRef); } catch (_) {}

        /* Dispatch a focus event without actually moving focus —
           handlers fire, but document.activeElement stays on our input
           so the iOS keyboard doesn't blink. */
        try { snizeInputRef.dispatchEvent(new FocusEvent('focus', { bubbles: true })); } catch (_) {}
        try { snizeInputRef.dispatchEvent(new FocusEvent('focusin', { bubbles: true })); } catch (_) {}

        /* If something stole focus during all that, return it. Sync —
           keyboard already open so iOS allows the refocus. */
        if (document.activeElement !== searchInput) {
          searchInput.focus({ preventScroll: true });
        }
      }

      /* 3. Now dispatch the FULL keystroke lifecycle that Searchanise
         can consume across versions. A bare Event('input') was
         insufficient on iOS Safari — some Searchanise builds need a
         real InputEvent with `inputType:'insertText'` and/or a
         focus/keydown/keypress lifecycle to mark the input as active
         before recomputing results. See dispatchSnizeKeystrokeLifecycle
         (defined above) for the exact event order. */
      dispatchSnizeKeystrokeLifecycle();

      /* CSS uses this class to show/hide the Searchanise overlay so
         results only appear after typing, not immediately on click. */
      document.body.classList.toggle('srail-search-has-query', !!searchInput.value.trim());

      if (!searchInput.value.trim()) {
        const clearBtn = document.querySelector('.snize-clear-button');
        if (clearBtn) clearBtn.click();
      }
    }

    const bridgeObserver = new MutationObserver(() => {
      if (!bridgeReady) initBridge();
    });
    bridgeObserver.observe(document.body, { childList: true, subtree: true });
    initBridge();

    /* ── Open search ───────────────────────────────────────── */
    searchTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      if (!searchInput) return;

      console.log('[SRAIL SEARCH] drawer open — click handler entered');

      searchForm.classList.add('is-open');
      searchTrigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('srail-search-open');

      /* Pre-activate Searchanise BEFORE focusing our input. Synthetic
         taps fire inside the trusted click-handler window so any
         Searchanise listener bound there sees them. Doing this BEFORE
         the focus call also means synthetic events can't disturb the
         keyboard once it's established. */
      activateSnizePanel();

      /* iOS Safari only opens the on-screen keyboard when .focus() runs
         SYNCHRONOUSLY inside the user-gesture handler. THIS LINE MUST
         REMAIN SYNCHRONOUS — do not wrap in setTimeout / promise. */
      searchInput.focus({ preventScroll: true });

      /* ── COMPREHENSIVE READINESS FLOW ─────────────────────────
         The synchronous activateSnizePanel above is a no-op if
         Searchanise's hidden input + overlay aren't both mounted yet
         (slow iOS network, lazy-loaded bundle). waitForSnizeReady
         polls every 50 ms for up to 1 s checking ALL of:
           • #snize-mobile-search-input exists & isConnected
           • #snize-instant-search-results overlay exists

         Once truly ready we run the full activation sequence:
           1. Cache snizeInputRef (initBridge — idempotent)
           2. Re-run activateSnizePanel (simulateTap is idempotent)
           3. Sync the visible input value into the hidden one
              (preserves anything the user already typed during wait)
           4. Dispatch the FULL keystroke lifecycle (input + change +
              keydown + keyup + focus + focusin) so Searchanise has
              every signal it might be listening for
           5. Focus-dance fallback: briefly focus the hidden snize
              input, then immediately restore focus to the visible
              input. Once the iOS keyboard is open, refocusing across
              inputs does NOT collapse it — but it DOES make
              Searchanise see a real focus event on its own input,
              which some builds require to begin reacting. */
      waitForSnizeReady(function (ok, state) {
        console.log('[SRAIL SEARCH] readiness resolved', {
          ok: ok,
          val: searchInput.value,
          overlay: !!(state && state.overlay)
        });
        if (!ok) return;

        /* 1 — cache ref via initBridge (no-op if already cached) */
        initBridge();

        /* 2 — run activation now that snize is fully mounted */
        activateSnizePanel();

        /* 3 — sync visible input value into hidden snize input */
        if (snizeInputRef) {
          snizeInputRef.value = searchInput.value;
          console.log('[SRAIL SEARCH] synced value into snize', {
            visible: searchInput.value,
            hidden:  snizeInputRef.value
          });
        }

        /* 4 — fire the full event lifecycle on snize input */
        dispatchSnizeKeystrokeLifecycle();

        /* 5 — focus-dance: deliver a trusted focus to Searchanise.
              searchInput.focus() is intentionally absent — the 50ms
              delayed steal in initBridge()'s focus-steal listener handles
              the return so Searchanise's async activation callback has time
              to check document.activeElement before we move focus away. */
        if (snizeInputRef) {
          try { snizeInputRef.focus({ preventScroll: true }); } catch (_) {}
        }

        /* If the user already typed during the wait, replay through
           the standard sync path so the keystroke lifecycle fires
           with up-to-date value semantics. */
        if (searchInput.value) syncToSnize();
      });

      /* iOS Safari predictive-search re-engagement.
         ─────────────────────────────────────────────
         iOS sometimes "loses" the focus engagement during the CSS
         drawer-slide-in transition (~250ms), so the predictive search
         JS doesn't initialise until the user taps again. After the
         transition settles (~100ms is enough — the drawer is partly
         visible, focus is honoured), re-focus to re-engage iOS, and
         dispatch an input event if there's already a value (browser
         autofill, restored session, etc.) so suggestions render
         without requiring a manual tap. Android & desktop don't
         need this but it's harmless on them — the second focus is
         a no-op and the conditional dispatch only fires when text
         exists. */
      setTimeout(() => {
        if (!searchInput) return;

        if (document.activeElement !== searchInput) {
          searchInput.focus({ preventScroll: true });
        }

        if (searchInput.value && searchInput.value.length > 0) {
          searchInput.dispatchEvent(new Event('input', { bubbles: true }));
          /* Mirror dispatch for iOS Safari — same fallback rationale as
             the `keyup` listener above. Some iOS builds fire `input`
             but Searchanise misses it during the drawer's slide-in
             transition; the parallel `keyup` always lands. */
          searchInput.dispatchEvent(new Event('keyup',  { bubbles: true }));
        }
      }, 100);
    });

    /**
     * Full-sequence tap simulation — a bare .click() only fires `click`
     * with isTrusted:false. Searchanise binds on mousedown/touchstart;
     * dispatching the full sequence covers whichever it listens to.
     */
    function simulateTap (el) {
      if (!el) return;
      const opts = { bubbles: true, cancelable: true, view: window };

      try {
        el.dispatchEvent(new TouchEvent('touchstart', {
          ...opts, touches: [], targetTouches: [], changedTouches: []
        }));
      } catch (_) {}
      try { el.dispatchEvent(new PointerEvent('pointerdown', opts)); } catch (_) {}
      el.dispatchEvent(new MouseEvent('mousedown', opts));
      el.dispatchEvent(new MouseEvent('mouseup',   opts));
      el.dispatchEvent(new MouseEvent('click',     opts));
      try { el.dispatchEvent(new PointerEvent('pointerup', opts)); } catch (_) {}
      try {
        el.dispatchEvent(new TouchEvent('touchend', {
          ...opts, touches: [], targetTouches: [], changedTouches: []
        }));
      } catch (_) {}
    }

    function activateSnizePanel () {
      /* simulateTap(searchInput) was REMOVED from this function.
         iOS Safari quirk: dispatching synthetic mousedown/click on our
         visible search input BEFORE .focus() runs causes iOS to treat
         the input as already-interacted-with and silently ignore the
         subsequent focus() — meaning the on-screen keyboard fails to
         open on the SECOND (and onward) drawer open after a close.
         The first open worked only because iOS hadn't yet seen the
         input in any state. After collapse(), the residual state from
         the first session's synthetic taps blocks the next focus.

         Searchanise still gets fully activated:
           - simulateTap(snizeInput) below primes its hidden-input
             listeners (no impact on our keyboard since snize is
             hidden + has no focusable surface for the iOS keyboard).
           - syncToSnize's first-keystroke fallback (see lines ~511-528)
             dispatches mousedown/mouseup/pointerdown/pointerup on
             the search BUTTON and a full simulateTap on snizeInput
             during the trusted real-keystroke gesture window. */
      const snizeInput = document.getElementById('snize-mobile-search-input');
      const snizeOverlay = document.getElementById('snize-instant-search-results');
      console.log('[SRAIL SEARCH] activateSnizePanel', {
        snizeInputPresent: !!snizeInput,
        snizeInputConnected: !!(snizeInput && snizeInput.isConnected),
        overlayPresent: !!snizeOverlay,
        bridgeReady: bridgeReady
      });
      if (snizeInput) simulateTap(snizeInput);
      /* No .focus() call here intentionally. The trigger-click handler
         above calls searchInput.focus() explicitly AFTER this runs, so
         the keyboard opens cleanly without us competing for focus.
         When this is invoked from syncToSnize's first-keystroke
         fallback, the user input gesture itself keeps the keyboard
         open — no manual refocus needed. */
    }

    /* ── Collapse: close our panel + reset Searchanise ───── */
    function collapse () {
      searchForm.classList.remove('is-open');
      searchTrigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('srail-search-open');
      document.body.classList.remove('srail-search-has-query');

      /* Reset activation so the next open re-primes Searchanise. */
      snizeActivated = false;

      if (searchInput) {
        searchInput.value = '';
        searchInput.blur();
      }

      if (snizeInputRef) {
        snizeInputRef.value = '';
        snizeInputRef.dispatchEvent(new Event('input', { bubbles: true }));
      }
      const clearBtn = document.querySelector('.snize-clear-button');
      if (clearBtn) clearBtn.click();

      const snizeClose = document.querySelector('.snize-close-button-arrow');
      if (snizeClose) snizeClose.click();
    }

    /* Close button inside the panel */
    document.querySelectorAll('[data-search-close]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        collapse();
      });
    });

    /* Esc to close */
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchForm.classList.contains('is-open')) {
        collapse();
      }
    });

    /* Watch for Searchanise overlay being removed from DOM (user closed it) */
    const mo = new MutationObserver(() => {
      if (window.innerWidth > 900) return;
      const overlay = document.getElementById('snize-instant-search-results');
      const overlayVisible = overlay && overlay.offsetParent !== null;
      if (!overlayVisible && searchForm.classList.contains('is-open') &&
          document.activeElement !== searchInput) {
        collapse();
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    /* Reset when viewport crosses into desktop */
    window.addEventListener('resize', () => {
      if (window.innerWidth > 900 && searchForm.classList.contains('is-open')) {
        collapse();
      }
    });
  }

})();
